import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Table, Modal, Form, Input, message, Tag, Tooltip, Popover } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, LinkOutlined, QrcodeOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';

const { TextArea } = Input;

const VideoManagement = () => {
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [linkModalVisible, setLinkModalVisible] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [currentVideoLink, setCurrentVideoLink] = useState('');
  const [videoForm] = Form.useForm();

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const response = await api.get('/learning/videos');
      if (response.data && response.data.success) {
        setVideos(response.data.data.videos || []);
      }
    } catch (error) {
      message.error('获取视频失败');
      console.error('获取视频失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoCreate = () => {
    setCurrentVideo(null);
    videoForm.resetFields();
    setVideoModalVisible(true);
  };

  const handleVideoEdit = (video) => {
    setCurrentVideo(video);
    videoForm.setFieldsValue({
      name: video.name,
      url: video.url,
      description: video.description,
      duration: video.duration
    });
    setVideoModalVisible(true);
  };

  const handleVideoDelete = async (id) => {
    try {
      await api.delete(`/learning/videos/${id}`);
      message.success('删除视频成功');
      fetchVideos();
    } catch (error) {
      message.error('删除视频失败');
      console.error('删除视频失败:', error);
    }
  };

  const handleVideoSubmit = async (values) => {
    try {
      if (currentVideo) {
        await api.put(`/learning/videos/${currentVideo.id}`, values);
        message.success('更新视频成功');
      } else {
        await api.post('/learning/videos', values);
        message.success('创建视频成功');
      }
      setVideoModalVisible(false);
      fetchVideos();
    } catch (error) {
      message.error('保存视频失败');
      console.error('保存视频失败:', error);
    }
  };

  const handleGenerateVideoLink = async (video) => {
    try {
      // 生成自动登录链接
      const response = await api.post('/learning/auto-login', { user_id: user.id });
      if (response.data && response.data.success) {
        const loginUrl = response.data.data.login_url;
        // 构建视频观看链接
        const videoLink = `${loginUrl}&video_id=${video.id}`;
        setCurrentVideoLink(videoLink);
        setLinkModalVisible(true);
      }
    } catch (error) {
      message.error('生成视频链接失败');
      console.error('生成视频链接失败:', error);
    }
  };

  const videoColumns = [
    {
      title: '视频名称',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      render: (url) => (
        <Tooltip title={url}>
          <span 
            style={{ 
              color: '#1890ff', 
              cursor: 'pointer',
              textDecoration: 'underline'
            }} 
            onClick={() => {
              navigator.clipboard.writeText(url);
              message.success('URL已复制到剪贴板');
            }}
          >
            {url}
          </span>
        </Tooltip>
      )
    },
    {
      title: '视频类型',
      dataIndex: 'type',
      key: 'type',
      render: (type) => <Tag>{type || '未知'}</Tag>
    },
    {
      title: '时长(秒)',
      dataIndex: 'duration',
      key: 'duration'
    },
    {
      title: '观看次数',
      dataIndex: 'view_count',
      key: 'view_count',
      render: (viewCount) => viewCount || 0
    },
    {
      title: '归属课程',
      key: 'courses',
      render: (_, record) => record.courses && record.courses.length > 0 ? (
        <div>
          {record.courses.map(course => (
            <span key={course.id} style={{ display: 'inline-block', marginRight: '8px', marginBottom: '4px' }}>
              <Tag>{course.name}</Tag>
            </span>
          ))}
        </div>
      ) : '无'
    },
    {
      title: '创建人',
      dataIndex: 'created_by_name',
      key: 'created_by_name'
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (createdAt) => new Date(createdAt).toLocaleString()
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button type="text" icon={<EditOutlined />} onClick={() => handleVideoEdit(record)} />
          <Tooltip title="生成观看链接">
            <Button type="text" icon={<LinkOutlined />} onClick={() => handleGenerateVideoLink(record)} />
          </Tooltip>
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleVideoDelete(record.id)} />
        </Space>
      )
    }
  ];

  return (
    <div className="video-management-container" style={{ padding: '10px 16px' }}>
      <div className="video-management-header" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: '24px', margin: 0 }}>视频管理</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleVideoCreate}>
          上传视频
        </Button>
      </div>

      {/* 视频管理 */}
      <Card style={{ marginBottom: '20px' }} title={
        <Space style={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>视频列表</span>
          <Button icon={<ReloadOutlined />} onClick={fetchVideos} loading={loading}>
            刷新
          </Button>
        </Space>
      }>
        <Table
          columns={videoColumns}
          dataSource={videos}
          rowKey="id"
          loading={loading}
        />
      </Card>

      {/* 视频模态框 */}
      <Modal
        title={currentVideo ? '编辑视频' : '上传视频'}
        open={videoModalVisible}
        onCancel={() => setVideoModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={videoForm}
          onFinish={handleVideoSubmit}
          layout="vertical"
        >
          <Form.Item
            label="视频名称"
            name="name"
            rules={[{ required: true, message: '请输入视频名称' }]}
          >
            <Input placeholder="请输入视频名称" />
          </Form.Item>

          <Form.Item
            label="视频URL"
            name="url"
            rules={[{ required: true, message: '请输入视频URL' }]}
          >
            <Input placeholder="请输入视频URL" />
          </Form.Item>

          <Form.Item
            label="视频描述"
            name="description"
          >
            <TextArea rows={4} placeholder="请输入视频描述" />
          </Form.Item>

          <Form.Item
            label="视频时长(秒)"
            name="duration"
          >
            <Input type="number" placeholder="请输入视频时长" />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setVideoModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                保存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 视频链接模态框 */}
      <Modal
        title="视频观看链接"
        open={linkModalVisible}
        onCancel={() => setLinkModalVisible(false)}
        footer={null}
        width={500}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p style={{ marginBottom: '16px' }}>点击或扫码下方链接即可观看视频</p>
          <div style={{ marginBottom: '16px', wordBreak: 'break-all' }}>
            <Input
              value={currentVideoLink}
              readOnly
              style={{ marginBottom: '8px' }}
            />
            <Button
              type="primary"
              onClick={() => {
                navigator.clipboard.writeText(currentVideoLink);
                message.success('链接已复制到剪贴板');
              }}
            >
              复制链接
            </Button>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(currentVideoLink)}`}
              alt="视频链接二维码"
              style={{ width: '200px', height: '200px' }}
            />
          </div>
          <p style={{ color: '#666', fontSize: '12px' }}>链接有效期为24小时</p>
        </div>
      </Modal>
    </div>
  );
};

export default VideoManagement;